<template>
	<div class="premium-left-box">
		<div
			v-for="offer in displayedOffers"
			:key="offer.store_id"
			class="premium-left-box__item"
			:class="[getPremiumLeftBoxClass(offer)]"
			@click="handleShopRedirect(offer)"
		>
			<template
				v-for="(heading, _index) in [getOfferHeading(offer)]"
				:key="_index"
			>
				<div class="premium-left-box__heading">
					<ShrinkText
						v-if="heading"
						:text="translate(heading)"
						:fontSize="14"
						isBold
					/>
				</div>
			</template>

			<PremiumLeftBoxProgressBar
				v-if="isProgressable(offer)"
				:offer="offer as ExtendedPiggyBankOffer"
			/>
			<Timer
				v-else-if="
					offer.end_date && !offer.attributes?.hide_remaining_time
				"
				class="premium-left-box__timer"
				:time="
					convertUnixTimeToRemainingSeconds(
						Math.round(
							new Date(offer.end_date as string).getTime() /
								1_000,
						),
					)
				"
				:has-icon="false"
				@timerFinished="handleLeftboxEnd(offer.store_id)"
			/>
			<span
				v-else-if="offer.attributes.multiplier_value"
				class="premium-left-box__text"
			>
				{{ offer.attributes.multiplier_value + "x Value" }}
			</span>
			<ShrinkText
				v-else
				class="premium-left-box__offer-name"
				:fontSize="12"
				isBold
				animatedOverflow
				overflowAnimation="scroll-reset"
				:text="translate(offer.name, true)"
			/>
		</div>
	</div>
</template>

<script lang="ts" setup>
import PremiumLeftBoxProgressBar from "@/components/shop/PremiumLeftBoxProgressBar.vue";
import ShrinkText from "@/components/ShrinkText.vue";
import Timer from "@/components/Timer.vue";
import { IS_DEV } from "@/config/constants";
import useSocketService from "@/hooks/useSocketService";
import {
	OfferAnalyticsClickSource,
	OfferAnalyticsType,
} from "@/libs/SocketService";
import {
	OfferProvider,
	type ExtendedPiggyBankOffer,
} from "@/services/OfferProvider";
import { PaymentProvider } from "@/services/PaymentProvider";
import useOfferStore from "@/store/useOfferStore";
import useQuestStore from "@/store/useQuestStore";
import useShopStore from "@/store/useShopStore";
import type { ActiveProvision } from "@/store/useUserStore";
import useUserStore from "@/store/useUserStore";
import { OfferSubtype, OfferTemplate, type ShopItem } from "@/types/shop";
import { convertUnixTimeToRemainingSeconds } from "@/utils/format";
import type { AllowedSkin } from "@/utils/shop/skins";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

const displayedOffers = ref<ShopItem[] | null>(null);

const offerStore = useOfferStore();
const { specialBuildingPack, currentOffer } = storeToRefs(offerStore);

const userStore = useUserStore();
const { activeProvisions } = storeToRefs(userStore);

/**
 * Dynamically calculates the maximum number of left box slots.
 * Adjusts the base limit based on active Ad and Gem Special Building offers.
 */
const MAX_LEFT_BOXES = computed((): number => {
	// TODO >> GD: flat value
	// Reference value: if changed to 3, all results shift down by 1
	const baseLimit = 4;

	const hasAdOffer = !!currentOffer.value;
	const hasGemSpecialBuilding = !!specialBuildingPack.value;

	// Logic:
	// - Both active: base - 2 (e.g., 4 -> 2)
	// - Only one active: base - 1 (e.g., 4 -> 3)
	// - Neither active: base (e.g., 4 -> 4)
	if (hasGemSpecialBuilding && hasAdOffer) {
		return baseLimit - 2;
	}

	if (hasGemSpecialBuilding || hasAdOffer) {
		return baseLimit - 1;
	}

	return baseLimit;
});

const ROTATION_INTERVAL = IS_DEV ? 0.3 * 60 * 1000 : 5 * 60 * 1000; // PROD >> 5 min in ms

const TEMPLATE_HEADINGS: Record<string, string> = {
	[OfferTemplate.PIGGY_BANK]: "ui.treasury",
};

const getOfferHeading = (offer: ShopItem): string | null => {
	const template = offer.attributes?.template;

	if (template && TEMPLATE_HEADINGS[template]) {
		return TEMPLATE_HEADINGS[template];
	}

	return null;
};

const isProgressable = (offer: ShopItem): boolean => {
	return offer && offer.attributes?.template === OfferTemplate.PIGGY_BANK;
};

const rotationOffset = ref<number>(0);
let rotationTimer: ReturnType<typeof setInterval> | null = null;

const shopStore = useShopStore();
const { activeItems, hiddenLeftBoxOffers, welcomeBackOffer } =
	storeToRefs(shopStore);

const questStore = useQuestStore();
const { tutorialActive } = storeToRefs(questStore);

// 1: STABLE sorted STRUCTURE
// Computed only re-runs when the rotation timer ticks or active shop items change
// It's NOT subscribed to the 'heartbeat' (e.g., piggy bank progress updates)
const sortedOfferIds = computed((): string[] => {
	// Note: We do NOT use OfferProvider.lastUpdated here
	if (tutorialActive.value || !activeItems.value) return [];

	// Use extended data just to check priority/visibility, but don't watch it for changes
	const extendedData = OfferProvider.extendedPiggyBankOffers.value || [];
	const extendedMap = new Map(
		extendedData.map(
			(ext: ExtendedPiggyBankOffer): [string, ExtendedPiggyBankOffer] => [
				ext.store_id,
				ext,
			],
		),
	);

	const baseItems = activeItems.value
		.map((item: ShopItem): ShopItem => {
			const ext = extendedMap.get(item.store_id);
			return ext ? { ...item, ...ext } : item;
		})
		.filter(
			(item: ShopItem): boolean =>
				!hiddenLeftBoxOffers.value.includes(item.store_id) &&
				item.show_in_left_box,
		);

	// Include welcomeBackOffer into base items rotation pool if valid and not already present
	if (welcomeBackOffer.value) {
		const wbItem = welcomeBackOffer.value;

		if (
			!hiddenLeftBoxOffers.value.includes(wbItem.store_id) &&
			wbItem.show_in_left_box &&
			!baseItems.some(
				(item: ShopItem): boolean => item.store_id === wbItem.store_id,
			)
		) {
			baseItems.push(wbItem);
		}
	}

	baseItems.sort((a: ShopItem, b: ShopItem): number => {
		if (a.attributes.high_priority !== b.attributes.high_priority) {
			return a.attributes.high_priority ? -1 : 1;
		}
		return (
			new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
		);
	});

	if (baseItems.length === 0) return [];

	const currentMax = MAX_LEFT_BOXES.value;
	if (baseItems.length <= currentMax) {
		return baseItems.map((i: ShopItem): string => i.store_id);
	}

	/////////////////////////////////////
	// Calculate rotation
	const highPriority = baseItems.filter(
		(i: ShopItem): boolean => !!i.attributes.high_priority,
	);
	const normalPriority = baseItems.filter(
		(i: ShopItem): boolean => !i.attributes.high_priority,
	);
	const availableSlots = Math.max(0, currentMax - highPriority.length);

	const rotatedNormalIds = [];
	for (let i = 0; i < availableSlots; i++) {
		const index = (rotationOffset.value + i) % normalPriority.length;
		rotatedNormalIds.push(normalPriority[index].store_id);
	}

	return [
		...highPriority.map((i: ShopItem): string => i.store_id),
		...rotatedNormalIds,
	].slice(0, currentMax);
	/////////////////////////////////////
});

// 2: REACTIVE CONTENT
// It takes the stable IDs and simply attaches the latest progress data
const offers = computed((): ShopItem[] | null => {
	// Subscribe to the heartbeat for progress bar updates
	void OfferProvider.lastUpdated.value;

	const ids = sortedOfferIds.value;
	const items = activeItems.value;

	if (!ids.length || !items) return null;

	const itemsMap = new Map(
		items.map((i: ShopItem): [string, ShopItem] => [i.store_id, i]),
	);

	// Include welcomeBackOffer in the items lookup map
	if (welcomeBackOffer.value) {
		const wbItem = welcomeBackOffer.value;

		// Create a modified copy of the welcome back offer with a custom skin in attributes
		const modifiedWbItem: ShopItem = {
			...wbItem,
			attributes: {
				...wbItem.attributes,
				skin: OfferSubtype.WelcomeBackOffer as unknown as AllowedSkin, // IMPORTANT
			},
		};
		itemsMap.set(modifiedWbItem.store_id, modifiedWbItem);
	}

	const extendedData = OfferProvider.extendedPiggyBankOffers.value ?? [];
	const extendedMap = new Map(
		extendedData.map(
			(ext: ExtendedPiggyBankOffer): [string, ExtendedPiggyBankOffer] => [
				ext.store_id,
				ext,
			],
		),
	);

	const result = ids.flatMap((id: string): ShopItem[] => {
		const originalItem = itemsMap.get(id);
		if (!originalItem) return [];

		const extendedItem = extendedMap.get(id);
		return [
			extendedItem ? { ...originalItem, ...extendedItem } : originalItem,
		];
	});

	return result.length > 0 ? result : null;
});

const OVERLAPLESS_SKINS = [
	"skin_resource_01",
	"skin_resource_02",
	"skin_storage_01",
	"skin_storage_02",
	"skin_exploration_01",
	"skin_exploration_02",
	"skin_people_01",
	"skin_people_02",
	"skin_food_01",
	"skin_food_02",
	"skin_coin_01",
	"skin_coin_02",
	"skin_building_01",
	"skin_building_02",
	"skin_level_up_01",
	"skin_level_up_02",
	"skin_gem_01",
	"skin_gem_02",
	"skin_gem_03",
	"skin_gem_04",
	"welcome_back_offer",
];

const getPremiumLeftBoxClass = (pack: ShopItem): string => {
	const base = "premium-left-box__skin";
	const postFix =
		pack.attributes.template === OfferTemplate.PIGGY_BANK
			? "--progress"
			: "";
	if (OVERLAPLESS_SKINS.includes(pack.attributes.skin ?? ""))
		return `${base}--${pack.attributes.skin}-60 premium-left-box__skin--overlapless`;
	return `${base}--${pack.attributes.skin + postFix}-70`;
};

const handleShopRedirect = async (offer: ShopItem): Promise<void> => {
	useSocketService().emitOfferAnalyticsSave({
		type: OfferAnalyticsType.OFFER_CLICK,
		clickSource: OfferAnalyticsClickSource.LEFTBOX ?? "UNKNOWN",
		storeId: offer.store_id ?? "UNKNOWN",
		offerName: offer.name ?? "UNKNOWN",
	});

	PaymentProvider.openPopup(offer.store_id, {
		clickSource: OfferAnalyticsClickSource.LEFTBOX,
	});
};

const handleLeftboxEnd = (storeId?: string): void => {
	if (!storeId) return;
	// Remove unavailable offers from left box and shop
	const currentItems = activeItems.value ?? [];

	if (storeId === welcomeBackOffer.value?.store_id) {
		useUserStore().setDeeplinkWelcomeBackOfferExpires(null);
		return;
	}

	if (storeId && currentItems.length > 0) {
		const filteredItems = currentItems.filter(
			(item: ShopItem): boolean => item.store_id !== storeId,
		);

		// Update the store only in case of a real change
		if (filteredItems.length !== currentItems.length) {
			shopStore.setItems(filteredItems);
		}
	}
};

const startRotationTimer = (): void => {
	rotationTimer = setInterval((): void => {
		rotationOffset.value++;
	}, ROTATION_INTERVAL);
};

////////
// NOTE: Display offer popups via delay seconds instead of action triggers
////////

// Keep track of IDs we've already scheduled or shown to prevent duplicates
const shownOfferIds = new Set<string | number>();
const activeTimers = new Map<string | number, ReturnType<typeof setTimeout>>();

const handleOfferDisplay = async (offer: ShopItem): Promise<void> => {
	// IMPORTANT: Unlimited offer, but cannot be repurchased while active
	if (offer.attributes?.template === OfferTemplate.PROVISION) {
		if (
			activeProvisions.value.some(
				(p: ActiveProvision): boolean => p.storeId === offer.store_id,
			)
		) {
			// NOTE: Should auto-trigger via popup_delay_seconds, but skipped because its rewards are currently active
			return;
		}
	}

	PaymentProvider.openPopup(offer.store_id, {
		clickSource: OfferAnalyticsClickSource.POPUP, // auto-open via popup_delay_seconds
	});

	// Mark as shown so it doesn't trigger again if the list updates
	shownOfferIds.add(offer.store_id);
};

watch(
	activeItems,
	(newOffers: ShopItem[] | null): void => {
		const currentOfferIds = new Set(
			newOffers?.map((o: ShopItem): string => o.store_id) || [],
		);

		for (const [id, timer] of activeTimers.entries()) {
			if (!currentOfferIds.has(id as string)) {
				clearTimeout(timer);
				activeTimers.delete(id);
			}
		}

		if (!newOffers) return;

		newOffers.forEach((offer: ShopItem): void => {
			const delay = offer.attributes?.popup_delay_seconds;
			const id = offer.store_id;

			// Only schedule if it has a delay, hasn't been shown, and isn't already ticking
			if (
				delay !== undefined &&
				!shownOfferIds.has(id) &&
				!activeTimers.has(id)
			) {
				const timer = setTimeout((): void => {
					handleOfferDisplay(offer);
					activeTimers.delete(id);
				}, delay * 1000); // Convert seconds to milliseconds

				activeTimers.set(id, timer);
			}
		});
	},
	{ immediate: true }, // Run on mount
);

watch(
	offers,
	(newVal: ShopItem[] | null): void => {
		displayedOffers.value = newVal;
	},
	{ deep: true, immediate: true },
);

onMounted((): void => {
	startRotationTimer();

	if (activeItems.value?.length) {
		OfferProvider.calculate();
	}
});

onUnmounted((): void => {
	activeTimers.forEach((timer: ReturnType<typeof setTimeout>): void =>
		clearTimeout(timer),
	);
	activeTimers.clear();

	if (rotationTimer) clearInterval(rotationTimer);
});
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "sass:list";
@use "sass:string";

.premium-left-box {
	position: relative;
	@include flex-col-center;
	align-items: flex-start;
	gap: 0.375rem;
	margin-left: 0.25rem;
	width: 4.375rem;

	$sizes: (60, 70);
	$overlapWidth: 160;

	// 1. BASE MIXINS FOR SHARED ASSETS & SIZES
	@mixin base-sprite-overlap-box($imageUrl, $baseHeight, $totalCount, $int) {
		$overlapRatio: calc($int / $overlapWidth);

		display: inline-block;
		background-image: url($imageUrl);
		background-size: (
				math.div(($overlapWidth * $overlapRatio) * $totalCount, 16) *
					1rem
			)
			(math.div(($baseHeight * $overlapRatio), 16) * 1rem);
		background-repeat: no-repeat;
		width: math.div($int, 16) * 1rem;
		height: math.div(calc($overlapRatio * $baseHeight), 16) * 1rem;
	}

	@mixin base-sprite-box(
		$imageUrl,
		$spriteHeight,
		$iconWidth,
		$totalCount,
		$int
	) {
		$scale: math.div($int, $iconWidth);

		display: inline-block;
		background-image: url($imageUrl);
		background-size: (math.div($int * $totalCount, 16)) * 1rem
			(math.div($spriteHeight * $scale, 16)) * 1rem;
		background-repeat: no-repeat;
		width: math.div($int, 16) * 1rem;
		height: math.div($spriteHeight * $scale, 16) * 1rem;
	}

	// 2. CENTRALIZED CONFIGURATION MAP
	$sprite-groups: (
		"classic": (
			"path": $path-images +
				"shop/left-boxes/leftbox-offer-sprite--h191.avif",
			"height": 191,
			"type": "overlap",
			"skins": (
				("skin_01", 0, 8),
				("skin_02", 1, 8),
				("skin_03", 2, 8),
				("skin_04", 3, 8),
				("skin_05", 4, 8),
				("skin_06", 5, 8),
				("skin_07", 6, 8),
				("skin_08", 7, 8),
			),
		),
		"classic2": (
			"path": $path-images +
				"shop/left-boxes/leftbox-offer-sprite-2--h170.avif",
			"height": 170,
			"icon-width": 140,
			"type": "standard",
			"skins": (
				("skin_resource_01", 0, 20),
				("skin_resource_02", 1, 20),
				("skin_storage_01", 2, 20),
				("skin_storage_02", 3, 20),
				("skin_exploration_01", 4, 20),
				("skin_exploration_02", 5, 20),
				("skin_people_01", 6, 20),
				("skin_people_02", 7, 20),
				("skin_food_01", 8, 20),
				("skin_food_02", 9, 20),
				("skin_coin_01", 10, 20),
				("skin_coin_02", 11, 20),
				("skin_building_01", 12, 20),
				("skin_building_02", 13, 20),
				("skin_level_up_01", 14, 20),
				("skin_level_up_02", 15, 20),
				("skin_gem_01", 16, 20),
				("skin_gem_02", 17, 20),
				("skin_gem_03", 18, 20),
				("skin_gem_04", 19, 20),
			),
		),
		"easter": (
			"path": $path-images +
				"shop/left-boxes/easter-event-leftbox-offer-sprite--h191.avif",
			"height": 191,
			"type": "overlap",
			"skins": (
				("event_skin_easter_01", 0, 3),
				("event_skin_easter_02", 0, 3),
				("event_skin_easter_03", 0, 3),
				("event_skin_easter_04", 0, 3),
				("event_skin_easter_05", 1, 3),
				("event_skin_easter_06", 1, 3),
				("event_skin_easter_07", 1, 3),
				("event_skin_easter_08", 1, 3),
				("event_skin_easter_09", 2, 3),
				("event_skin_easter_10", 2, 3),
				("event_skin_easter_11", 2, 3),
				("event_skin_easter_12", 2, 3),
			),
		),
		"fishing": (
			"path": $path-images +
				"shop/left-boxes/fishing-event-leftbox-offer-sprite--h208.avif",
			"height": 208,
			"type": "overlap",
			"skins": (
				("event_skin_fishing_01", 0, 8),
				("event_skin_fishing_02", 1, 8),
				("event_skin_fishing_03", 2, 8),
				("event_skin_fishing_04", 3, 8),
				("event_skin_fishing_05", 4, 8),
				("event_skin_fishing_06", 5, 8),
				("event_skin_fishing_07", 6, 8),
				("event_skin_fishing_08", 7, 8),
			),
		),
		"firefest": (
			"path": $path-images +
				"shop/left-boxes/firefest-event-leftbox-offer-sprite--h208.avif",
			"height": 208,
			"type": "overlap",
			"skins": (
				("event_skin_firefest_01", 0, 12),
				("event_skin_firefest_02", 1, 12),
				("event_skin_firefest_03", 2, 12),
				("event_skin_firefest_04", 3, 12),
				("event_skin_firefest_05", 4, 12),
				("event_skin_firefest_06", 5, 12),
				("event_skin_firefest_07", 6, 12),
				("event_skin_firefest_08", 7, 12),
				("event_skin_firefest_09", 8, 12),
				("event_skin_firefest_10", 9, 12),
				("event_skin_firefest_11", 10, 12),
				("event_skin_firefest_12", 11, 12),
			),
		),
		"solarfair": (
			"path": $path-images +
				"shop/left-boxes/solarfair-event-leftbox-offer-sprite--h208.avif",
			"height": 208,
			"type": "overlap",
			"skins": (
				("event_skin_solarfair_01", 0, 12),
				("event_skin_solarfair_02", 1, 12),
				("event_skin_solarfair_03", 2, 12),
				("event_skin_solarfair_04", 3, 12),
				("event_skin_solarfair_05", 4, 12),
				("event_skin_solarfair_06", 5, 12),
				("event_skin_solarfair_07", 6, 12),
				("event_skin_solarfair_08", 7, 12),
				("event_skin_solarfair_09", 8, 12),
				("event_skin_solarfair_10", 9, 12),
				("event_skin_solarfair_11", 10, 12),
				("event_skin_solarfair_12", 11, 12),
			),
		),
		"hunting": (
			"path": $path-images +
				"shop/left-boxes/hunting-event-leftbox-offer-sprite--h208.avif",
			"height": 208,
			"type": "overlap",
			"skins": (
				("event_skin_hunting_01", 0, 12),
				("event_skin_hunting_02", 1, 12),
				("event_skin_hunting_03", 2, 12),
				("event_skin_hunting_04", 3, 12),
				("event_skin_hunting_05", 4, 12),
				("event_skin_hunting_06", 5, 12),
				("event_skin_hunting_07", 6, 12),
				("event_skin_hunting_08", 7, 12),
				("event_skin_hunting_09", 8, 12),
				("event_skin_hunting_10", 9, 12),
				("event_skin_hunting_11", 10, 12),
				("event_skin_hunting_12", 11, 12),
			),
		),
		"halloween": (
			"path": $path-images +
				"shop/left-boxes/halloween-event-leftbox-offer-sprite--h208.avif",
			"height": 208,
			"type": "overlap",
			"skins": (
				("event_skin_halloween_01", 0, 12),
				("event_skin_halloween_02", 1, 12),
				("event_skin_halloween_03", 2, 12),
				("event_skin_halloween_04", 3, 12),
				("event_skin_halloween_05", 4, 12),
				("event_skin_halloween_06", 5, 12),
				("event_skin_halloween_07", 6, 12),
				("event_skin_halloween_08", 7, 12),
				("event_skin_halloween_09", 8, 12),
				("event_skin_halloween_10", 9, 12),
				("event_skin_halloween_11", 10, 12),
				("event_skin_halloween_12", 11, 12),
			),
		),
		"pirates": (
			"path": $path-images +
				"shop/left-boxes/pirates-event-leftbox-offer-sprite--h208.avif",
			"height": 208,
			"type": "overlap",
			"skins": (
				("event_skin_pirates_01", 0, 12),
				("event_skin_pirates_02", 1, 12),
				("event_skin_pirates_03", 2, 12),
				("event_skin_pirates_04", 3, 12),
				("event_skin_pirates_05", 4, 12),
				("event_skin_pirates_06", 5, 12),
				("event_skin_pirates_07", 6, 12),
				("event_skin_pirates_08", 7, 12),
				("event_skin_pirates_09", 8, 12),
				("event_skin_pirates_10", 9, 12),
				("event_skin_pirates_11", 10, 12),
				("event_skin_pirates_12", 11, 12),
			),
		),
		"piggy": (
			"path": $path-images +
				"shop/left-boxes/piggy-bank-leftbox-offer-sprite--h208-progress.avif",
			"height": 208,
			"type": "overlap",
			"skins": (
				("piggy_bank_gem_skin_01--progress", 0, 1),
			),
		),
		"deeplink": (
			"path": $path-images +
				"shop/left-boxes/deeplink-leftbox-offer-sprite--h170.avif",
			"height": 170,
			"icon-width": 140,
			"type": "standard",
			"skins": (
				("welcome_back_offer", 0, 1),
			),
		),
	);

	&__skin {
		&--overlapless {
			margin: 0 auto;
		}
	}

	&__item {
		position: relative;
		cursor: pointer;
		@include clickable-element;

		&--overflow-normalized {
			left: -0.313rem;
		}

		.special-building-icon {
			position: absolute;
			bottom: 1.15rem;
			left: 0;
		}
	}

	// 3. DYNAMIC GENERATION BY GROUP (Using namespaced modules)
	@each $int in $sizes {
		@each $groupName, $groupData in $sprite-groups {
			$path: map.get($groupData, "path");
			$height: map.get($groupData, "height");
			$type: map.get($groupData, "type");
			$skins: map.get($groupData, "skins");

			$maxCount: 1;
			@if list.length($skins) > 0 {
				$maxCount: list.nth(list.nth($skins, 1), 3);
			}

			$selectors: ();
			@each $skin in $skins {
				$skinName: list.nth($skin, 1);
				$selectors: list.append(
					$selectors,
					string.unquote(
						".premium-left-box__skin--#{$skinName}-#{$int}"
					),
					comma
				);
			}

			#{$selectors} {
				@if $type == "standard" {
					$iconWidth: map.get($groupData, "icon-width");
					@include base-sprite-box(
						$path,
						$height,
						$iconWidth,
						$maxCount,
						$int
					);
				} @else {
					@include base-sprite-overlap-box(
						$path,
						$height,
						$maxCount,
						$int
					);
				}
			}

			@each $skin in $skins {
				$skinName: list.nth($skin, 1);
				$pos: list.nth($skin, 2);

				.premium-left-box__skin--#{$skinName}-#{$int} {
					@if $type == "standard" {
						background-position: -(math.div($int * $pos, 16)) *
							1rem
							0;
					} @else {
						background-position: (-(math.div($int, 16)) * $pos) *
							1rem
							0;
					}
				}
			}
		}
	}

	&__heading {
		position: absolute;
		@include flex-row-center;
		top: 0;
		left: 0;
		padding: 0 0.5rem;
		width: 100%;
		height: 1.125rem;
		color: #f4d278;
	}

	&__timer,
	&__offer-name,
	&__text {
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
		height: 1.313rem;
		color: #f2dcb5;
		font-size: 0.75rem;
		text-align: center;
	}

	&__timer {
		height: 1.5rem;
	}

	&__offer-name {
		padding: 0 0.5rem;

		:deep(.shrink-text) {
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		}
	}
}
</style>
